import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import styled from 'styled-components'
import CopyToClipboard from 'react-copy-to-clipboard'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { useParams } from 'react-router-dom'
import { plusComma } from '../../js/script'
import { handlePostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { BtnCouponDownload, BtnDetailIcon } from '../Button'
import ProductCouponModal from './ProductCouponModal'
import { DiscountedPrice, ProductSalePrice } from './ProductItem'
import ProductDiscountInfo from './ProductDiscountInfo'
import { useAppContext } from '../provider/AppProvider'
import { useCommonContext } from '../provider/CommonProvider'
import { useMyContext } from '../provider/MyProvider'
import { ProductDetailType, ProductDetailBaseInfo, ProductDetailStickerInfo } from '../../@types/product'
import { IssuableCouponType } from '../../@types/coupon'

interface ProductPriceInfoMobileProps {
  product: ProductDetailType
  productItem: ProductDetailBaseInfo | undefined
  starCountArray: number[]
  couponSort: IssuableCouponType[]
  couponDown: boolean
  setCouponDown: React.Dispatch<React.SetStateAction<boolean>>
  setCouponCheck: React.Dispatch<React.SetStateAction<boolean>>
  setCheckDown: React.Dispatch<React.SetStateAction<boolean>>
  disCountAcc: number
  osCheck: string
}

// 이미지 경로
const imgURL = '/images'

const ProductPriceInfoMobile = ({
  product,
  productItem,
  starCountArray,
  couponSort,
  couponDown,
  setCouponDown,
  setCouponCheck,
  setCheckDown,
  disCountAcc,
  osCheck,
}: ProductPriceInfoMobileProps) => {
  const [textStickerInfo, setTextStickerInfo] = useState<ProductDetailStickerInfo[]>([])

  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()
  const { setToastMessage, setIsToast } = useMyContext()

  const param = useParams()

  const getStarImage = useCallback((idx: number, reviewRate: number) => {
    if (idx + 1 > Math.ceil(reviewRate)) return `${imgURL}/star_off.svg`
    return reviewRate - idx === 0.5 ? `${imgURL}/star_half_off.svg` : `${imgURL}/star.svg`
  }, [])

  const handleCopy = useCallback(
    async (url: string) => {
      try {
        if (osCheck !== 'Android OS') {
          setIsToast(true)
          setToastMessage('URL이 복사되었습니다.')
        }
      } catch (err) {
        console.error('Failed to copy: ', '복사안됨', err)
      }
    },
    [osCheck, setIsToast, setToastMessage]
  )

  const createShareEventInfo = useCallback(
    (url: string) => ({
      method: url,
      item_id: productItem?.productNo,
      item_name: productItem?.productName,
    }),
    [productItem]
  )

  const trackShareEvent = useCallback(
    (eventInfo: any) => {
      const shareClick: DataLayerArgs = {
        dataLayer: {
          event: 'share',
          event_info: eventInfo,
        },
      }

      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: 'share',
          eventType: eventInfo,
        })
      } else {
        TagManager.dataLayer(shareClick)
      }
    },
    [isAppView]
  )

  const handleShareClick = useCallback(() => {
    const url = `${window.location.href}?app=${isAppView}&direct=true`
    const eventInfo = createShareEventInfo(url)

    handlePostMessage({
      actionType: 'shareUrl',
      params: { url },
    })

    trackShareEvent(eventInfo)
  }, [createShareEventInfo, isAppView, trackShareEvent])

  const handleShareCopy = useCallback(() => {
    const url = `${window.location.href}?app=${isMobile}&share=true&direct=true`
    const eventInfo = createShareEventInfo(`${window.location.href}?app=${isAppView}&direct=true`)

    handleCopy(url)
    trackShareEvent(eventInfo)
  }, [createShareEventInfo, handleCopy, isAppView, trackShareEvent])

  const starImages = useMemo(
    () =>
      starCountArray.map((element: number, idx: number) => (
        <img src={getStarImage(idx, product.reviewRate)} alt='별점' key={idx} />
      )),
    [getStarImage, product.reviewRate, starCountArray]
  )

  useEffect(() => {
    if (productItem) {
      const filteredTextStickers = productItem?.stickerInfos?.filter((el: any) => el.type === 'TEXT') || []
      setTextStickerInfo(filteredTextStickers) // 상태를 한 번만 업데이트
    }
  }, [productItem])

  return (
    <>
      {/* 제품 상단 정보 */}
      <ProductTopInfo>
        {/* 제목 wrap */}
        <ProductTitleWrap>
          <div>
            {/* 브랜드 제목 */}
            {product.brand?.name && <ProductBrandTitle>{product.brand?.name}</ProductBrandTitle>}
            {/* 상품 제목 */}
            <ProductTitle>{productItem?.productName}</ProductTitle>
            {textStickerInfo.length > 0 && (
              <div className='item_txt_flag_detail'>
                {textStickerInfo.map((el: any) => (
                  <span key={el.label} className='delivery'>
                    {el.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </ProductTitleWrap>

        {/* 공유하기, 찜하기 아이콘 */}
        <ProductDetailIcons>
          <BtnDetailIcon>
            {isAppView ? (
              <img src={`${imgURL}/icon_share.png`} alt='공유하기' onClick={handleShareClick} />
            ) : (
              <CopyToClipboard
                text={`${window.location.href}?app=${isMobile}&share=true&direct=true`}
                onCopy={handleShareCopy}
              >
                <img src={`${imgURL}/icon_share.png`} alt='공유하기' />
              </CopyToClipboard>
            )}
          </BtnDetailIcon>
        </ProductDetailIcons>
      </ProductTopInfo>

      <ProductInfoWrap>
        <div className='rating_review_coupon'>
          {/* 별점, 리뷰 */}
          <StarRatingDetail>
            <p className='star_rating'>{starImages}</p>
            <p>{product?.reviewRate}</p>
            <p className='review'>{`리뷰 ${product.counter ? product.counter?.reviewCnt : 0}건`}</p>
          </StarRatingDetail>

          {/* 가격 */}
          <ProductDetailPriceInfo>
            <ProductDetailPrice>
              {Number(product.price?.immediateDiscountAmt) !== 0 && (
                <ProductDetailSalePrice>
                  {product.price ? plusComma(product?.price?.salePrice) : 0}원
                </ProductDetailSalePrice>
              )}
              <ProductDetailDiscountedPrice>
                {Number(product.price?.immediateDiscountAmt) !== 0 && (
                  <p className='percent'>
                    {product.price
                      ? Math.floor((product.price?.immediateDiscountAmt / product.price?.salePrice) * 100)
                      : 0}
                    %
                  </p>
                )}
                <p>{product.price ? plusComma(product.price?.salePrice - product.price?.immediateDiscountAmt) : 0}원</p>
                {/* 쿠폰받기 */}
                <div className='coupon_download_wrap'>
                  {hasAccessToken && couponSort?.length !== 0 && (
                    <BtnCouponDownload
                      onClick={() => {
                        setCouponDown(true)
                      }}
                    >
                      <img src={`${imgURL}/icon_download.png`} alt='다운로드' />
                      쿠폰받기
                    </BtnCouponDownload>
                  )}
                </div>
              </ProductDetailDiscountedPrice>
              {couponDown && (
                <ProductCouponModal
                  downloadable={couponSort}
                  setCouponCheck={setCouponCheck}
                  setCheckDown={setCheckDown}
                  setIsModal={setCouponDown}
                  headerText={'쿠폰받기'}
                  selectProductNo={param.id}
                />
              )}

              {/* 예상 적립 마일리지 */}
              {hasAccessToken && product.price?.accumulationAmtWhenBuyConfirm !== 0 && (
                <div className='mileage_npay_wrap'>
                  <div className='save_mileage'>
                    <p>페이백 적립금</p>
                    <p>{product.price ? plusComma(product.price?.accumulationAmtWhenBuyConfirm) : 0}</p>
                    <img src='/images/icon_won.svg' alt='coin' style={{ width: 14, height: 14 }} />
                  </div>
                </div>
              )}
            </ProductDetailPrice>
          </ProductDetailPriceInfo>
        </div>
      </ProductInfoWrap>

      {/* 할인 예상 정보 */}
      <ProductDiscountInfo product={product} productItem={productItem} disCountAcc={disCountAcc} />
    </>
  )
}

export default memo(ProductPriceInfoMobile)

// 상품 상단 정보 wrap (브랜드, 상품명, 공유하기, 찜하기 아이콘)
export const ProductTopInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 2px;
`
// 상품 상단 정보(브랜드, 상품명)
export const ProductTitleWrap = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1 1;
`

export const ProductInfoWrap = styled.div`
  display: flex;
  flex-direction: row;
  & .rating_review_coupon {
    flex: 1;
  }
`

// 상품 브랜드명
export const ProductBrandTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  margin-bottom: 10px;
`
// 상품 제목
export const ProductTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`
// 상품 상세 아이콘 wrap
export const ProductDetailIcons = styled.div``

// 하단 아이콘 버튼 영역
export const BtnDetailIconWrap = styled.div`
  display: flex;
`

export const StarRatingDetail = styled.div`
  display: flex;
  gap: 4px;
  & p {
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }
  & p.star_rating {
    display: flex;
    align-items: center;
  }
  & p.star_rating > img {
    width: 14px;
    height: 14px;
  }
  & p.review {
    margin-left: 6px;
    color: #898989;
  }
`

// 가격, 쿠폰받기 영역
export const ProductDetailPriceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`

export const ProductDetailPrice = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-flow: column;
  gap: 0;
  margin-top: 16px;
  & .mileage_npay_wrap {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    & .npay {
      width: 37px;
      & img {
        width: 100%;
      }
    }
  }
  @media screen and (min-width: 768px) {
    & .mileage_npay_wrap {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
      & .npay {
        width: 37px;
        margin-top: 4px;
        & img {
          width: 100%;
        }
      }
    }
  }
`
export const ProductDetailSalePrice = styled(ProductSalePrice)`
  font-size: 13px;
  margin-bottom: 4px;
`
export const ProductDetailDiscountedPrice = styled(DiscountedPrice)`
  position: relative;
  & p {
    font-size: 16px;
    line-height: 20px;
  }
  & .coupon_download_wrap {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 1;
  }
`
